import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getApiUrl } from '../../api'

interface NoticeListItem {
  notice_id: number
  author_id: number | null
  title: string
  created_at: string
  thumbnail_url: string | null
}

const PAGE_SIZE = 6

export default function NoticeList() {
  const navigate = useNavigate()
  const [notices, setNotices] = useState<NoticeListItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [searchTitle, setSearchTitle] = useState('')
  const [inputTitle, setInputTitle] = useState('')
  const [page, setPage] = useState(1)

  useEffect(() => {
    fetchNotices()
  }, [searchTitle, page])

  const handleSearch = () => {
    setPage(1)
    setSearchTitle(inputTitle)
  }

  const fetchNotices = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (searchTitle) params.append('title', searchTitle)
      params.append('skip', String((page - 1) * PAGE_SIZE))
      params.append('limit', String(PAGE_SIZE))

      const res = await fetch(getApiUrl(`/marubit/api/notice/list?${params.toString()}`))
      if (!res.ok) throw new Error('조회 실패')
      const data = await res.json()
      setNotices(data.notices)
      setTotal(data.total)
    } catch {
      alert('목록을 불러오는 데 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    })

  const totalPages = Math.ceil(total / PAGE_SIZE)

  const getPageNumbers = () => {
    const start = Math.max(1, page - 2)
    const end = Math.min(totalPages, start + 4)
    return Array.from({ length: end - start + 1 }, (_, i) => start + i)
  }

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center text-gray-300">
      불러오는 중...
    </div>
  )

  return (
    <div className="bg-[#fafafa] min-h-screen px-6 py-12">
      <div className="max-w-[1200px] mx-auto">

        {/* 타이틀 */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-[26px] font-extrabold text-[#5a4035] mb-2">공지사항</h1>
            <p className="text-[14px] text-gray-400">총 {total}건</p>
          </div>
          {/* <button
            onClick={() => navigate('/notice/register')}
            className="px-5 py-2 bg-[#887267] text-white text-[14px] font-bold rounded-lg hover:bg-[#6b5248] transition-colors"
          >
            + 공지 등록
          </button> */}
        </div>

        {/* 목록 */}
        {notices.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center text-gray-300 f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)]">
            등록된 공지사항이 없습니다.
          </div>
        ) : (
          <div className="bg-white rounded-2xl f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)] overflow-hidden">
            {notices.map((notice, idx) => (
              <div
                key={notice.notice_id}
                onClick={() => navigate(`/notice/${notice.notice_id}`)}
                className={[
                  'flex items-center gap-4 px-6 py-4 cursor-pointer hover:bg-[#f5f2f0] transition-colors',
                  idx < notices.length - 1 ? 'border-b border-gray-100' : '',
                ].join(' ')}
              >
                {/* 썸네일 */}
                <div className="w-[60px] h-[60px] rounded-lg overflow-hidden bg-gray-50 border border-gray-100 shrink-0 flex items-center justify-center">
                  {notice.thumbnail_url ? (
                    <img src={notice.thumbnail_url} alt={notice.title} className="w-full h-full object-cover" />
                  ) : (
                    <svg viewBox="0 0 24 24" width={24} height={24} fill="none" stroke="#ddd" strokeWidth={1.5}>
                      <rect x="3" y="3" width="18" height="18" rx="2"/>
                      <circle cx="8.5" cy="8.5" r="1.5"/>
                      <polyline points="21 15 16 10 5 21"/>
                    </svg>
                  )}
                </div>

                {/* 내용 */}
                <div className="flex-1 min-w-0">
                  <p className="text-[15px] font-semibold text-[#222] truncate">{notice.title}</p>
                  <p className="text-[12px] text-gray-400 mt-1">{formatDate(notice.created_at)}</p>
                </div>

                {/* 화살표 */}
                <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="#ccc" strokeWidth={2} className="shrink-0">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </div>
            ))}
          </div>
        )}

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8 mb-6">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:border-[#887267] hover:text-[#887267] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              ‹
            </button>
            {getPageNumbers().map(num => (
              <button
                key={num}
                onClick={() => setPage(num)}
                className={[
                  'w-9 h-9 flex items-center justify-center rounded-lg text-[14px] font-medium transition-colors',
                  num === page
                    ? 'bg-[#887267] text-white border border-[#887267]'
                    : 'border border-gray-200 text-gray-500 hover:border-[#887267] hover:text-[#887267]',
                ].join(' ')}
              >
                {num}
              </button>
            ))}
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:border-[#887267] hover:text-[#887267] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              ›
            </button>
          </div>
        )}

        {/* 검색 */}
        <div className="bg-white rounded-2xl p-5 mt-6 max-w-[600px] mx-auto f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)] flex gap-3">
          <input
            type="text"
            placeholder="공지사항 검색"
            value={inputTitle}
            onChange={e => setInputTitle(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            className="flex-1 px-4 py-2 text-[14px] border-[1.5px] border-gray-200 rounded-[10px] outline-none focus:border-[#887267] transition-colors"
          />
          <button
            onClick={handleSearch}
            className="px-5 py-2 bg-[#887267] text-white text-[14px] font-bold rounded-lg hover:bg-[#6b5248] transition-colors"
          >
            검색
          </button>
        </div>
      </div>
    </div>
  )
}